import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { crmApi, formatAED } from "@/lib/crm";
import { useSettings, stageBadgeClass } from "@/contexts/SettingsContext";
import NewLeadDialog from "@/components/crm/NewLeadDialog";

export default function Pipeline() {
  const { settings } = useSettings();
  const stages = settings.stages || [];
  // "Lost" (last common stage) hidden from board. Any stage with color 'rose' also hidden.
  const pipelineStages = stages.filter((s) => s.id !== "lost" && s.color !== "rose");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLeadStage, setNewLeadStage] = useState(null);

  const load = () => {
    setLoading(true);
    crmApi.listLeads({ limit: 500 }).then(setLeads).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const firstLabel = pipelineStages[0]?.label || "New Enquiry";
  const grouped = pipelineStages.reduce((acc, s) => {
    acc[s.label] = leads.filter((l) => (l.status || firstLabel) === s.label);
    return acc;
  }, {});

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;

    const newStatus = destination.droppableId;
    const lead = leads.find((l) => l.id === draggableId);
    if (!lead) return;

    setLeads((prev) => prev.map((l) => (l.id === draggableId ? { ...l, status: newStatus } : l)));
    try {
      await crmApi.updateLead(draggableId, { status: newStatus });
      toast.success(`Moved “${lead.full_name}” to ${newStatus}`);
    } catch {
      toast.error("Failed to update status");
      load();
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6 px-2 flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-overline uppercase text-neutral-500">Sales</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl">Pipeline</h1>
        </div>
        <button
          onClick={() => setNewLeadStage("New Enquiry")}
          data-testid="pipeline-new-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase hover:opacity-90"
        >
          <Plus size={14}/> Add lead
        </button>
      </div>

      {loading ? (
        <div className="text-xs tracking-overline uppercase text-neutral-500 px-2">Loading pipeline…</div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-8 px-2" data-testid="kanban-board">
            {pipelineStages.map((stage) => {
              const cards = grouped[stage.label] || [];
              const total = cards.reduce((s, c) => s + (c.proposal_value || 0), 0);
              return (
                <Droppable droppableId={stage.label} key={stage.id}>
                  {(dropProvided, snap) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      data-testid={`kanban-col-${stage.id}`}
                      className={`w-80 min-w-[320px] flex flex-col border ${snap.isDraggingOver ? "border-neutral-900 dark:border-white" : "border-neutral-200 dark:border-neutral-800"} bg-neutral-50 dark:bg-neutral-950 transition-colors`}
                    >
                      <div className={`p-3 border-b ${stageBadgeClass(stage.color)} border-current/20`}>
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] tracking-overline uppercase font-medium">{stage.label}</div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setNewLeadStage(stage.label)}
                              data-testid={`pipeline-add-${stage.id}`}
                              className="p-1 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                              title="Add lead to this stage"
                            >
                              <Plus size={12}/>
                            </button>
                            <div className="text-xs tabular-nums">{cards.length}</div>
                          </div>
                        </div>
                        <div className="text-[11px] tabular-nums mt-0.5 opacity-70">{formatAED(total)}</div>
                      </div>
                      <div className="p-2 flex-1 min-h-[80vh] space-y-2">
                        {cards.map((card, idx) => (
                          <Draggable draggableId={card.id} index={idx} key={card.id}>
                            {(dragProvided, dragSnap) => (
                              <Link
                                to={`/crm/leads/${card.id}`}
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                data-testid={`kanban-card-${card.id}`}
                                className={`block p-3 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 shadow-sm hover:-translate-y-0.5 transition-transform ${dragSnap.isDragging ? "shadow-lg" : ""}`}
                              >
                                <div className="text-sm font-medium truncate">{card.full_name}</div>
                                <div className="text-xs text-neutral-500 truncate mt-0.5">{card.service}</div>
                                {card.proposal_value ? (
                                  <div className="mt-2 text-xs tabular-nums font-medium">{formatAED(card.proposal_value)}</div>
                                ) : null}
                              </Link>
                            )}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      )}

      <NewLeadDialog
        open={!!newLeadStage}
        defaultStatus={newLeadStage}
        onClose={() => setNewLeadStage(null)}
        onCreated={load}
      />
    </div>
  );
}
